import styled, { css, keyframes } from 'styled-components';
import { baseText, SubTitle } from 'core/styles';
import { OverlayTrigger } from 'core/components/FullMobileOverlay';
import { centeredTitle } from 'core/components/FullMobileOverlay/fullMobileOverlayStyles';
import { spinnerStyles } from 'core/components/Spinner';
import {
	getColor,
	Fonts,
	typeScale,
	StyleVars,
	maxContainer,
	grid,
	gridTemplateCols,
} from 'core/utils';
import { hideScroll } from 'styled-manila';

const SIDEBAR_WIDTH = 220;

const slideyBar = keyframes`
	0% { width: 5%; }
	6% { width: 25%; }
	96% { width: 100%; }
`;

const formControl = css`
	${baseText}
	font-size: 14px;
	font-weight: normal;
	display: block;
	width: 100%;
	height: 33px;
	padding: 6px 12px;
	color: ${getColor('gray')};
	vertical-align: middle;
	background-color: ${getColor('white')};
	border: 1px solid ${getColor('gray2')};
	border-radius: 4px;
	box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
	transition: all ease-in-out .15s;
`;

const checkTreatment = css`
	/* child + highlight gets special treatment: icon-check.svg */
	background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2215%22%20height%3D%2212%22%20viewBox%3D%220%200%2015%2012%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5.01009%2011.7643L0.216014%206.53442C-0.0720048%206.22021%20-0.0720048%205.71077%200.216014%205.39654L1.25904%204.25865C1.54706%203.94442%202.01408%203.94442%202.3021%204.25865L5.53162%207.78174L12.4489%200.235652C12.7369%20-0.0785507%2013.2039%20-0.0785507%2013.492%200.235652L14.535%201.37353C14.823%201.68774%2014.823%202.19718%2014.535%202.51141L6.05315%2011.7644C5.7651%2012.0786%205.29811%2012.0786%205.01009%2011.7643Z%22%20fill%3D%22%23333333%22%2F%3E%3C%2Fsvg%3E');
	background-repeat: no-repeat;

	/* size & position of icon */
	background-size: 0.75em auto;
	background-position: right 0.7em top 50%;

	/* if you edit size, maybe add more padding-right so text doesn’t overlap */
	padding-right: 1.5em;
`;

const baseSelect = css`
	${formControl}
	appearance: none;
	border-radius: 2px;
	box-shadow: none;
	color: ${getColor('gray9')};

	/* arrow icon via icon-caret.svg */
	background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%228%22%20height%3D%225%22%20viewBox%3D%220%200%208%205%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M3.6891%204.51134L0.128783%201.11283C-0.0429278%200.948927%20-0.0429278%200.683194%200.128783%200.519305L0.544038%200.122926C0.715456%20-0.0407008%200.993275%20-0.0410157%201.1651%200.122226L4.00001%202.8156L6.8349%200.122226C7.00672%20-0.0410157%207.28454%20-0.0407008%207.45596%200.122926L7.87122%200.519305C8.04293%200.683211%208.04293%200.948945%207.87122%201.11283L4.31091%204.51134C4.1392%204.67523%203.86082%204.67523%203.6891%204.51134Z%22%20fill%3D%22%234F4F4F%22%2F%3E%3C%2Fsvg%3E');
	background-repeat: no-repeat;

	/* size & position of arrow icon */
	background-size: 0.65em auto;
	background-position: right 0.7em top 50%;

	/* if you edit size, also may want more padding-right so the <select> text doesn’t overlap */
	padding-right: 1.4em;

	/* IE10-11: hide the menu icon pseudo element */
	::-ms-expand {
		display: none;
	}

	&:hover {
		border-color: ${getColor('gray8')};
	}

	&:focus {
		border-color: ${getColor("gray")};
		color: ${getColor("gray")};
		outline: none;
	}
`;

export const SidebarWrapper = styled.div`
	width: auto;
`;

export const SidebarSection = styled.div`
	margin-bottom: 24px;

	${props => !props.noBorder && css`
		padding-bottom: 16px;
		border-bottom: 1px solid ${getColor('alto')};
	`}
`;

export const CatNavSection = styled(SidebarSection)`
	font-family: ${Fonts.serif};
`;

export const SidebarTitle = styled.div`
	font-size: 12px;
	font-weight: 600;
	margin-bottom: 8px;
`;

export const FilterTrigger = styled(OverlayTrigger)`
	${baseSelect}

	/* filter icon via icon-filter.svg */
	background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%228%22%20height%3D%228%22%20viewBox%3D%220%200%208%208%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M6.98924%200H0.344149C0.0388136%200%20-0.115258%200.404156%200.10109%200.640172L2.75%203.53033V6.75C2.75%206.87236%202.80473%206.98703%202.89662%207.05722L4.04245%207.9319C4.26851%208.10453%204.58333%207.92958%204.58333%207.62468V3.53033L7.23231%200.640172C7.44821%200.404625%207.2952%200%206.98924%200Z%22%20fill%3D%22%234A4A4A%22%2F%3E%3C%2Fsvg%3E');
`;

export const FilterSelect = styled.select`
	${baseSelect}
`;

export const FilterTitle = styled(SubTitle)`
	${centeredTitle}
`;

export const FooterClose = styled.label`
	font-size: 19px;
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: 0.2px;
	text-align: center;
	display: block;
	margin: 0 auto;
`;

export const FilterContainer = styled.div`
	margin-bottom: 12px;
`;

const selectedTag = css`
	background-color: ${getColor('porcelain')};
	border-color: ${getColor('gray')};
`;

export const FilterTagLink = styled.a`
	${baseText}
	position: relative;
	display: block;
	font-size: 14px;
	line-height: 18px;
	margin-bottom: 8px;

	${props => props.pill && css`
		display: inline-block;
		border: 1px solid ${getColor('alto')};
		padding: 6px 8px;
		min-width: 44px; /* touchability */
		text-align: center;

		&:active,
		&:focus {
			color: ${getColor('gray')};
		}
	`}

	${props => props.pill && props.selected && css`
		${selectedTag}
	`}

	/* non-pill styled has adjacent check to mark active */
	${props => !props.pill && props.selected && css`
		${checkTreatment}
		font-weight: bold;
		background-position: left 0 top 50%;
		padding-left: 1.25em;
		padding-right: 0;
	`}

	/* show spinner after the text link */
	${props => !props.pill && props.isLoading && css`
		&:after {
			content: "";
			position: absolute;
			${spinnerStyles}
			animation-delay: 0.3s;
			margin-left: ${typeScale(1)};
		}
	`}

	/* show loading bar at bottom of pill */
	${props => props.pill && props.isLoading && css`
		&:after {
			content: "";
			position: absolute;
			height: 2px;
			width: 5%;
			background-color: ${getColor('primaryBrand')};
			bottom: 0;
			left: 0;
			animation: ${slideyBar} 3s ease-in-out infinite;
		}
	`}
`;

export const CheckWrapper = styled.div`
	margin-bottom: 8px;
`;

export const FilterTagContainer = styled.div`
	/* This provides spacing between FilterTagLink elements */
	letter-spacing: 4px;
`;

export const ListingResults = styled.div`
	margin-top: ${typeScale(1.5)};
	${maxContainer(StyleVars.screenCjDesktopXxl)}

	@media (min-width: ${StyleVars.screenSm}) {
		margin-top: ${typeScale(3)};
		display: grid;
		grid-template-columns: ${SIDEBAR_WIDTH}px 1fr;
		grid-template-rows: auto 1fr;
		grid-gap: ${typeScale(4)};
		grid-template-areas:
			"sidebar page-header"
			"sidebar main-content";
	}

	#DiscoverySidebar {
		grid-area: sidebar;
		${hideScroll};
		scrollbar-width: none;

		@media (max-width: ${StyleVars.desktopScreenSm}) {
			margin-right: -16px;
			margin-left: -16px;
			padding: 0 16px;
		}
	}

	#PageHeader {
		grid-area: page-header;

		@media (max-width: ${StyleVars.screenSm}) {
			margin: 0 -16px;
		}
	}
	#MainContent {
		grid-area: main-content;
	}
`;

export const ListingResultsRow = styled.div`
	${grid('1fr', '1fr')}
	grid-gap: ${typeScale(3)} 0;
	margin-bottom: ${typeScale(3)};

	@media (min-width: 345px) {
		${gridTemplateCols(`1fr 1fr`)}
		grid-gap: ${typeScale(4)} ${typeScale(3)};
		margin-bottom: ${typeScale(4)};
	}

	@media (min-width: 1366px) {
		${gridTemplateCols(`1fr 1fr 1fr`)}
		grid-gap: ${typeScale(4)} ${typeScale(3)};
		margin-bottom: ${typeScale(4)};
	}

	@media (min-width: 1600px) {
		${gridTemplateCols(`1fr 1fr 1fr 1fr`)}
	}
`;

export const CuratedListingsTitle = styled.div`
	font-size: 38px;
	font-weight: 800;
`;

export const MobileCuratedListingsTitle = styled.div`
	font-size: 27px;
	font-weight: 800;
	width: calc(100% - 18px);
	margin-top: 44px;
	margin-left: 16px;
	margin-right: 16px;
`;

export const CuratedTitleSection = styled.div`
	display: flex;
	flex-direction: column;
	text-decoration: none;
	padding: 16px;
	margin: auto 0;
	justify-content: space-between;

	@media(max-width: ${StyleVars.screenMd}) {
		width: 267px;
		flex-basis: 267px;
		max-width: 267px;
		min-width: 267px;

		/* Show the last gap to the right */
		&:last-child {
			position: relative;

			&:after {
				content: '';
				width: 32px;
				height: 100%;
				display: block;
				position: absolute;
				top: 0;
				right: -32px;
			}
		}

	}
	width: 340px;
`;


export const CuratedBoxesWrapper = styled.div`
	grid-row: ${props => props.isMobile ? `5` : `3`};
	grid-column: 1/-1;

	background: ${getColor('cratejoyTeal')};
	padding: 0;  /* Needs to be 0 so the scroll gradient looks good */
	color: ${getColor('white')};
	font-size: 20px;
	overflow: hidden;
	position: relative;
	width: ${props => props.isMobile ? `calc(100% + 32px)` : `100%`};

	${props => props.isMobile
		? css`
			margin: 0 -16px;
			background-image: linear-gradient(rgba(255,255,255,0.01) 50px,rgba(255,255,255,0.01) 150px,rgba(255,255,255) 190px,rgb(255,255,255),rgb(255,255,255));
		`
		: css`
			border-radius: 10px;
			@media (max-width: 1375px) {
				width: calc(100% + 32px);
				border-top-right-radius: 0px;
				border-bottom-right-radius: 0px;
			}
			${props.backgroundImage && css`
				background-image: url(${props.backgroundImage});
				background-size: cover;
				background-repeat: no-repeat;
			`}
	`}

	${props => props.fadeOut && css`
		&:before {
			content: '';
			width: 120px;
			height: 100%;
			top: 0;
			right: 0;
			position: absolute;
			z-index: 1;

			@media (max-width: 767px) {
				width: 60px;
			}
		}
	`}
`;

export const CuratedBoxes = styled.div`
	display: flex;
	flex-wrap: nowrap;
	overflow-x: auto;
	-webkit-overflow-scrolling: touch;

	${hideScroll};

	margin: 4px 0;
`;

export const CuratedBox = styled.div`
	display: flex;
	flex-direction: column;

	width: 245px;
	flex-basis: 245px;
	max-width: 245px;
	min-width: 245px;

	cursor: pointer;
	background: ${getColor('white')};
	border-radius: 10px;
	box-shadow: -2px 4px 10px rgba(51, 51, 51, 0.05);
	margin: 16px;
	padding: 0;
`;

export const ListingDetails = styled.div`
	padding: 16px;
	border-bottom-left-radius: 10px;
	border-bottom-right-radius: 10px;
	${props => props.isMobile && `border: 1px solid ${getColor("concreteGray")}`}
	padding-bottom: ${props => props.isMobile ? `12px` : `16px`};
`;

export const PriceContainer = styled.div`
	font-size: 14px;
	color: ${getColor('easternBlue')};
	font-weight: 700;
`;

export const OldPrice = styled.span`
	font-size: 12px;
	color: ${getColor('gray4')};
	text-decoration: line-through;
	font-weight: 600;
`;

export const Subtitle = styled.a`
	color: ${props => props.isMobile ? getColor('black') : getColor('white')};
	text-decoration: underline;
	font-size: ${props => props.isMobile ? `16px` : `20px`};
	font-weight: 600;
	margin-left: ${props => props.isMobile ? `16px` : `0`};
`;

export const Spacer = styled.div`
	width: 16px;
`;

export const CuratedCollectionsContainer = styled.div`
	grid-row: 1;
	grid-column: 1/-1;

	background: ${getColor('concreteGray')};
	display: flex;
	flex-wrap: wrap;
	justify-content: space-evenly;
	padding: ${props => props.isMobile ? `8px` : `4px`};

	${props => props.isMobile && `margin: 0 -16px`};
	margin-bottom: 30px;
`;

export const CuratedCollectionsTitle = styled.div`
	margin: ${props => props.isMobile ? `8px 24px` : `24px`};
	flex-grow: 4;
	flex-basis: 100%;
	font-size: ${props => props.isMobile ? `22px` : `26px`};
	font-weight: 700;
`;

export const CuratedCollectionWrapper = styled.a`
	padding: ${props => props.isMobile ? `16px 24px` : `12px`};

	flex-basis: ${props => props.numItems ? `calc(100% / ${props.numItems})` : `25%`};
	max-width: 400px;

	@media(max-width: ${StyleVars.screenMd}) {
		flex-basis: 50%;
		flex-basis: ${props => props.numItems === 1 ? `100%` : `50%`};
	}
`;

export const CuratedCollectionTitle = styled.div`
	font-size: ${props => props.isMobile ? `14px` : `21px`};
	font-weight: 600;
	color: ${getColor('black')};
`;

export const CuratedCollectionImage = styled.img`
	width: 100%;
	height: auto;
`;

export const Description = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	margin: 8px 0 4px;
	line-height: @line-height-base;
	font-size: ${props => props.isMobile ? `13px` : `13px`};
	max-height: 3 * (@font-size-base * @line-height-base);

	p {
		max-height: 110px;
		overflow: hidden;
		margin-left: 0px;
	}
`;

export const BottomSectionWrapper = styled.div`
	${maxContainer(StyleVars.screenCjDesktopLg)}
	padding-top: ${typeScale(6)};
	padding-bottom: ${typeScale(4)};
`;

export const FilterControls = styled.div`
	display: grid;
	overflow-x: auto;
	${hideScroll};
	scrollbar-width: none;
	-webkit-overflow-scrolling: touch;
	grid-gap: 8px;
	grid-auto-flow: column;
	margin-left: -16px;
	margin-right: -16px;
	margin-bottom: ${typeScale(1.5)};
	padding: 6px 16px;
	background-color: transparent;

	${props => props.isSticky && css`
		position: fixed;
		top: 0;
		z-index: 2;
		width: 100%;
		background-color: ${getColor("white")};
	`}
`;

export const FilterBarFilterIcon = styled(OverlayTrigger)`
	font-size: 14px;
	font-weight: 600;
	border: 2px solid;
	border-radius: 4px;
	color: ${getColor("oxfordBlue")};
	padding: 2px 10px;
	height: 30px;
	display: inline-flex;
	max-width: 80px;
	position: relative;
`;

export const IconWrapper = styled.div`
	position: relative;
	min-width: 14px;
	margin-right: 11px;
	display: flex;
	align-items: center;
	${({ hasFilters }) => hasFilters && css`
		&:after {
			content: '';
			position: absolute;
			top: -4px;
			right: -4px;
			z-index: 1;
			width: 10px;
			height: 10px;
			border-radius: 50%;
			background: ${getColor("cornflowerBlue")};
			border: 2px solid white;
		}
	`}
`;
